import type {
  AcceptMatchRequest,
  AcceptMatchResponse,
  AttachBarcodeRequest,
  Barcode,
  LookupResponse,
  ScanQueueItem,
  ScanQueueStatus,
} from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, toQuery } from '../../lib/api';

/** Everything a successful scan can change. */
function useScanInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of ['series', 'collection', 'stats', 'scan-queue', 'publishers']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
}

export function useScanLookup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; addon?: string | null; forceRemote?: boolean }) =>
      api.post<LookupResponse>('/lookup', {
        code: input.code,
        addon: input.addon ?? undefined,
        forceRemote: input.forceRemote ?? false,
      }),
    // A miss lands in the queue, so its badge may change.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['scan-queue'] }),
  });
}

export function useAcceptMatch() {
  const invalidate = useScanInvalidation();
  return useMutation({
    mutationFn: (request: AcceptMatchRequest) =>
      api.post<AcceptMatchResponse>('/scan/accept', request),
    onSuccess: invalidate,
  });
}

export function useAttachBarcode() {
  const invalidate = useScanInvalidation();
  return useMutation({
    mutationFn: ({ issueId, ...request }: AttachBarcodeRequest & { issueId: string }) =>
      api.post<Barcode>(`/issues/${issueId}/barcodes`, request),
    onSuccess: invalidate,
  });
}

export function useScanQueue(status: ScanQueueStatus | 'all') {
  return useQuery({
    queryKey: ['scan-queue', 'list', status],
    queryFn: () =>
      api.get<ScanQueueItem[]>(
        `/scan-queue${toQuery({ status: status === 'all' ? undefined : status })}`,
      ),
  });
}

export function useScanQueueCount() {
  return useQuery({
    queryKey: ['scan-queue', 'count'],
    queryFn: () => api.get<{ open: number }>('/scan-queue/count'),
    staleTime: 30_000,
  });
}

export function useUpdateQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ScanQueueStatus }) =>
      api.patch<ScanQueueItem>(`/scan-queue/${id}`, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['scan-queue'] }),
  });
}

export function useDeleteQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/scan-queue/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['scan-queue'] }),
  });
}

export function useRetryQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<LookupResponse>(`/scan-queue/${id}/retry`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['scan-queue'] }),
  });
}
